import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UsersCollectionService } from 'src/app/store/store-services';
import { AppStateService } from 'src/app/store/app-state.service';
import { debounceTime, map, shareReplay, switchMap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { UserDataModel } from '@modules/auth';
import { MessengerService } from '../messenger.service';
import { Router } from '@angular/router';
import { HttpUserProfileService } from '@services/HttpServices/UserProfileHttpService/http-user-profile.service';
import { Store } from '@ngrx/store';
import { UserListModel } from '@modules/admin/_models/user-list.model';
import { AdminService } from '@modules/admin/_services/admin.service';
import { PagedResponseModel } from '@models/Generic/paged-response.model';

@Component({
  selector: 'app-new-chat-user-search',
  templateUrl: './new-chat-user-search.component.html',
  styleUrls: ['./new-chat-user-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewChatUserSearchComponent implements OnInit, OnDestroy {

  @ViewChild('searchPan', {static: true}) searchPan!: TemplateRef<HTMLElement>;
  public searchForm: FormGroup;
  public searchKey = '';

  @Output() onClick: EventEmitter<void> = new EventEmitter();

  @Input() private onlineUsers: Observable<UserDataModel[]>;
  public userList$: Observable<UserListModel[]>;
  public allUsers$: Observable<UserListModel[]>;
  public searchQuery$: BehaviorSubject<void> = new BehaviorSubject(undefined);

  private blockedByUsers$: Observable<string[]>;

  constructor(
    private adminService: AdminService,
    public httpUserProfileService: HttpUserProfileService,
    private fb: FormBuilder,
    public messengerService: MessengerService,
    private router: Router,
  ) {
    this.blockedByUsers$ = this.httpUserProfileService.getBlockedByUsersList().pipe(
      shareReplay(1),
    );
  }

  public ngOnInit(): void {
    this.initForm();

    this.allUsers$ = this.searchQuery$.pipe(
      debounceTime(50),
      switchMap(() => this.adminService.getAllUsers(1, 1000, this.form.searchKey.value)),
      map((response: PagedResponseModel<UserListModel>) => response.data),
    )

    this.userList$ = combineLatest([
      this.allUsers$, 
      this.onlineUsers,
      this.blockedByUsers$
    ]).pipe(
      map(([users, onlineUsers, blockedByList]) => {
        if (!this.form?.searchKey?.value) 
          return onlineUsers?.map((user) => ({
            ...user,
            id: +user.id,
            status: true,
            statusLabel: '',
            roles: user.roles?.[0],
          } as UserListModel)).sort((a: UserListModel, b: UserListModel) => {
            return a.userName.localeCompare(b.userName)
          }).map((user) => ({
            ...user,
            canSendMessage: !blockedByList.find((id) => id === user.userId),
          }));

        return users.map((user) => ({
          ...user,
          canSendMessage: !blockedByList.find((id) => id === user.userId),
        }));
      })
    );
  }

  public ngOnDestroy(): void {

  }

  public openUrl(url: string) {
    this.onClick.next();
    this.router.navigateByUrl(url)
  }

  get form() {
    return this.searchForm.controls;
  }

  public onInput() {
    this.searchQuery$.next(undefined);
  }
  
  public resetSearch() {
    this.form.searchKey.setValue('');
    this.searchQuery$.next();
  }

  public getUniqueName(userId) {
    return this.messengerService.getOne2OneConversationUniqueName(userId);
  }

  private initForm() {
    this.searchForm = this.fb.group({
      searchKey: [''],
    });
  }
}
